/*
 *
 *  * Copyright(c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */
import { Injectable } from '@angular/core';
import { Logger, MessageService, ModalService } from '@core/lib/services';
import { NotifyService } from '@app/common/notify.service';
import { TranslateService } from '@ngx-translate/core';
import { UserPreferenceService } from '@app/common/user-preference.service';
import { AppConfigService } from '@app/common/app-config.service';
import { SearchFilterCriteriaService } from '@app/ui/search/search-filter-criteria.service';
import { filter, map, mapTo } from 'rxjs/operators';
import { Observable, of, ReplaySubject, Subject } from 'rxjs';
import { REPORT_STATUS } from '@app/di/integrations/reporting/reporting-contants';
import { EventMessage, Events, EventService, EventType } from '@app/common/event';
import { EventWorkItem } from '@app/data-model/event-work-item';
import { AuditService } from '@app/common/audit.service';
import { ModalRef } from '@core/public-api';
import { UtilService } from '@app/util/util.service';
import { CoReaderDialogComponent } from '@app/common/modals/components/co-reader/co-reader-dialog.component';
import { DialogType } from '@app/di/integrations/dialog-type';
import { TabContextService } from '@app/ui/client-cache';

import * as lodash from 'lodash';
import { appConstants } from '@app/common/app-constants';
import { IntegrationStatusService } from '@app/di/integration-status.service';
import { NPRDataService } from '@app/di/integrations/utils/npr-bridge.service';

const log = new Logger('NativeAcademicWorkflowService');

@Injectable({
  providedIn: 'root'
})
export class NativeAcademicWorkflowService {
  private static readonly IGNORE_NAW_DURING_AA = 'IGNORE_NAW_DURING_AA';

  private readonly currentUserName: string;
  private previousCoReader: string;

  private isUserSkippedDefaultCoReaderSelection = false;

  private assigneesLoaded$: Subject<any> = new ReplaySubject<boolean>(1);

  private data = {};

  private defaultCoReaderOverrides = new Map<number, string>();

  private modals = new Map<number, ModalRef>();

  constructor(
    private userPreferenceService: UserPreferenceService,
    private appConfigService: AppConfigService,
    private searchFilterCriteriaService: SearchFilterCriteriaService,
    private notify: NotifyService,
    private messageService: MessageService,
    private auditService: AuditService,
    private eventService: EventService,
    private translate: TranslateService,
    private modalService: ModalService,
    private utilService: UtilService,
    private tabContextService: TabContextService,
    private integrationStatusService: IntegrationStatusService,
    private nprDataService: NPRDataService
  ) {
    this.beforeEvents();
    this.afterEvents();

    this.eventService.registerNawInstituteCheckCallback((workItem: any) => this.checkCoReporterAccess(workItem));

    this.currentUserName = this.appConfigService.getUserId();

    const sub = this.messageService.on('preloadSearchCriteria.done').subscribe(() => {
      this.assigneesLoaded$.next(true);
      sub.unsubscribe();
    });

    this.messageService.on('userPreferenceUpdated::autoadvance').subscribe(() => this.checkUserPreferenceChanges());
    this.messageService
      .on('userPreferenceUpdated::nativeAcademicWorkflow')
      .subscribe(() => this.checkUserPreferenceChanges());
  }

  showNAWReassignWarningDialog(workItems, asPromise?: boolean): Observable<boolean> | Promise<boolean> {
    if (!this.isAppConfigActive()) {
      return asPromise ? Promise.resolve(true) : of(true);
    }

    if (!this.containsNawExam(workItems)) {
      return asPromise ? Promise.resolve(true) : of(true);
    }

    const modalResult$ = this.modalService
      .openModal(this.translate.instant('naw.reassign.acknowledgementDialog.description'), {
        headerContent: this.translate.instant('naw.reassign.acknowledgementDialog.header'),
        disableHeaderCloseBtn: true,
        enableYesBtn: true,
        isDecisionModal: true,
        noButtonLabel: this.translate.instant('label.cancel'),
        yesButtonLabel: this.translate.instant('label.continue')
      })
      .afterClosed.pipe(map(answer => answer === 'YES'));

    return asPromise ? modalResult$.toPromise() : modalResult$;
  }

  showNativeAcademicWorkflowDialog(
    workItem: any,
    newReportStatus,
    associationList,
    removedFromAssociation,
    associatedExamsFromDB,
    examContext
  ) {
    if (!this.isActive()) {
      return;
    }

    const data = {
      dataSource: undefined,
      dialogType: DialogType.COREADER
    };

    let modalRef: ModalRef = undefined;

    if (newReportStatus === REPORT_STATUS.PRELIM) {
      data.dataSource = this.getNawUserList(workItem, data.dialogType);
      modalRef = this.openModal(workItem, data, 'naw.coreaderdialog.header');
    } else if (newReportStatus === REPORT_STATUS.DRAFT && this.isInCoReportingWorklist(workItem)) {
      data.dialogType = DialogType.REJECT;
      data.dataSource = this.getNawUserList(workItem, data.dialogType);
      modalRef = this.openModal(workItem, data, 'naw.rejectdialog.header');
    }

    modalRef?.afterClosed.subscribe(selection => {
      // In case of Ignore or close the associated exams have to be handled
      if (!selection) {
        this.handleAssociation(associationList, removedFromAssociation, workItem, associatedExamsFromDB, examContext);
      }

      if (selection?.userName) {
        this.selectCoReaderOrReject(
          workItem,
          selection.userName,
          data.dialogType === DialogType.REJECT,
          associationList,
          removedFromAssociation
        );
      }
    });
  }

  private handleAssociation(
    associationList,
    removedFromAssociation,
    workItem: any,
    associatedExamsFromDB,
    examContext
  ) {
    // in this case the associations cannot change
    if (examContext === 'preview' || examContext === 'launchreadonly') {
      return;
    }

    let origCount = associatedExamsFromDB?.length;
    let newCount = associationList?.length;
    origCount = lodash.isNil(origCount) || origCount === 0 ? null : origCount;
    newCount = lodash.isNil(newCount) || origCount === 0 ? null : newCount;

    const assocChanged = (removedFromAssociation && removedFromAssociation.length > 0) || origCount !== newCount;
    if (this.containsNawExam(workItem) && assocChanged) {
      const state = this.getState(workItem);
      this.selectCoReaderOrReject(
        workItem,
        state.nawState.owner.userName,
        state.nawState.type === Events.rejectReport.value,
        associationList,
        removedFromAssociation
      );
    }
  }

  isAppConfigActive(): boolean {
    return this.appConfigService.getAppConfigByKey('nativeAcademicWorkflow') === 'true';
  }

  isActive(): boolean {
    return this.isAppConfigActive() && this.userPreferenceService.userPreferences.nativeAcademicWorkflow;
  }

  containsNawExam(workItems, assignmentRequired?: boolean): boolean {
    // if the app config is off, then no exam counts as NAW exam
    if (!this.isAppConfigActive()) {
      return false;
    }

    if (Array.isArray(workItems)) {
      return (workItems as any[]).findIndex(item => this.isNawExam(item, assignmentRequired)) !== -1;
    } else if (workItems) {
      return this.isNawExam(workItems, assignmentRequired);
    } else {
      return false;
    }
  }

  private isNawExam(workItem, assignmentRequired?: boolean): boolean {
    const state = this.getState(workItem);
    return !!state?.nawState && (assignmentRequired ? state?.isAssigned : true);
  }

  isInCoReportingWorklist(workItem): boolean {
    const state = this.getState(workItem);
    return state?.nawState?.type === Events.selectCoReader.value;
  }

  private getState(workItem) {
    return workItem?.state || workItem?.keys?.state;
  }

  private getUsersWithAccess(workItem): any[] {
    const instituteId = typeof workItem?.instituteId === 'number' ? workItem.instituteId : workItem?.keys?.instituteId;
    const isVIP = workItem?.patientVipCode || workItem?.keys?.patientVipCode;

    return this.searchFilterCriteriaService
      .getAssignee(isVIP === 'Y')
      .filter(rad => rad.userName !== this.currentUserName)
      .filter(
        rad =>
          !this.appConfigService.isInstituteSeparationEnabled() ||
          workItem === undefined ||
          !Array.isArray(rad.instituteIds) ||
          rad.instituteIds.includes(instituteId)
      );
  }

  getNawUserList(workItem, dialogType: DialogType): Observable<any> {
    return this.assigneesLoaded$.pipe(
      filter(Boolean),
      mapTo(this.getUsersWithAccess(workItem)),
      map(rads => (!!rads ? rads : [])),
      map(rads => {
        switch (dialogType) {
          case DialogType.REJECT:
            return this.moveFront(rads, this.getNawStateCreator(workItem));
          case DialogType.DEFAULT_COREADER:
            const defaultCoReader = this.userPreferenceService.getUserLevelPreferenceFromCache('defaultCoReader');
            return this.moveFront(rads, defaultCoReader);
          case DialogType.COREADER:
          case DialogType.OVERRIDE_DEFAULT_COREADER:
            return this.moveFront(rads, this.previousCoReader);
          default:
            return rads;
        }
      })
    );
  }

  private moveFront(rads: any[], userName: string): any[] {
    rads.forEach(rad => (rad['preSelected'] = false));

    if (userName) {
      const prioritizedUserIndex = rads.findIndex(rad => rad.userName === userName);
      if (prioritizedUserIndex !== -1) {
        const reorderedList = [...rads.splice(prioritizedUserIndex, 1), ...rads];
        reorderedList[0]['preSelected'] = true;
        return reorderedList;
      }
    }
    return rads;
  }

  selectCoReaderOrReject(workItem, selectedUserName, reject: boolean, associationList, removedAssociationList) {
    const auditData = this.createAuditData(workItem, selectedUserName);
    if (!reject) {
      this.previousCoReader = selectedUserName;
    }

    const nawEventWorkItem = new EventWorkItem(workItem.id, { id: workItem.id });

    const params = { userName: selectedUserName, examAudit: auditData };
    if (removedAssociationList) {
      params['removedAssociationList'] = removedAssociationList;
    }

    if (associationList) {
      params['addedAssociationList'] = associationList;
    }

    this.eventService
      .sendEvent(new EventMessage(reject ? Events.rejectReport : Events.selectCoReader, nawEventWorkItem, params))
      .catch(e => {
        const errorMsg = reject
          ? this.translate.instant('naw.rejectdialog.fail')
          : this.translate.instant('naw.coreaderdialog.fail');
        log.error(errorMsg, e);
        this.notify.error(errorMsg);
      });
  }

  private createAuditData(workItem, selectedUserName) {
    let auditData: any = this.auditService.getExamAuditInfo(workItem);
    auditData = auditData ? { ...auditData, ownerName: selectedUserName } : { ownerName: selectedUserName };
    return auditData;
  }

  private defaultCoreaderModalOpen = false;

  private checkUserPreferenceChanges() {
    const autoAdvanceActive =
      this.userPreferenceService.userPreferences.autoAdvanceActive ||
      this.userPreferenceService.userPreferences.autoAdvanceAssigned;
    const defaultCoReaderExists = !!this.userPreferenceService.getUserLevelPreferenceFromCache('defaultCoReader');

    if (
      !this.defaultCoreaderModalOpen &&
      autoAdvanceActive &&
      this.userPreferenceService.userPreferences.nativeAcademicWorkflow
    ) {
      const workItem = this.utilService.getActiveTab()?.workItem;

      if (workItem && workItem.id !== -1 && this.appConfigService.isInstituteSeparationEnabled()) {
        this.checkCoReporterAccess(workItem, true);
      } else {
        if (!defaultCoReaderExists) {
          this.defaultCoreaderModalOpen = true;
          this.openModal(
            workItem,
            {
              dataSource: this.getNawUserList(undefined, DialogType.DEFAULT_COREADER),
              dialogType: DialogType.DEFAULT_COREADER
            },
            'naw.coreaderdialog.header'
          ).afterClosed.subscribe(selection => {
            const username = selection?.userName || '';
            this.isUserSkippedDefaultCoReaderSelection = !username;
            this.userPreferenceService.createOrUpdateUserLevelPreference({ defaultCoReader: username }, true);
            this.defaultCoreaderModalOpen = false;
          });
        }
      }
    }
  }

  private isNawAndAutoAdvanceActive(): boolean {
    return (
      (this.userPreferenceService.userPreferences.autoAdvanceActive ||
        this.userPreferenceService.userPreferences.autoAdvanceAssigned) &&
      this.userPreferenceService.userPreferences.nativeAcademicWorkflow
    );
  }

  /**
   * It returns default coreader username.
   * If AA is not activated, return empty string.
   *
   * @returns returns default coreader username
   */
  private getDefaultCoreader(): string {
    const coreader = this.userPreferenceService.getUserLevelPreferenceFromCache('defaultCoReader');

    if (this.isNawAndAutoAdvanceActive()) {
      return coreader || NativeAcademicWorkflowService.IGNORE_NAW_DURING_AA;
    }

    return '';
  }

  private beforeEvents() {
    this.eventService.beforeEvent(Events.close.name, (lifecycle, workItem) => {
      this.handleBeforeCloseEvents(workItem, lifecycle);
      this.cleanupModals(workItem.id);
    });

    this.eventService.beforeEvent(Events.closediscard.name, (lifecycle, workItem) => {
      this.handleBeforeCloseEvents(workItem, lifecycle);
      this.cleanupModals(workItem.id);
    });
  }

  public async checkCoReporterAccess(workItem, triggeredByUserPrefChange = false) {
    this.defaultCoReaderOverrides.delete(workItem?.id);
    const defaultCoReader = this.getDefaultCoreader();

    // we only check the access if AA and NAW is active, institute separation is enabled and there is an active workitem
    // also, if the user already skipped the default co-reader selection, then we do not check again
    // except if it was triggered by user preference change, then we give another chance to select a default co-reader
    if (
      this.isNawAndAutoAdvanceActive() &&
      this.appConfigService.isInstituteSeparationEnabled() &&
      (triggeredByUserPrefChange ||
        this.getDefaultCoreader() != NativeAcademicWorkflowService.IGNORE_NAW_DURING_AA ||
        !this.isUserSkippedDefaultCoReaderSelection) &&
      workItem?.id !== -1
    ) {
      const usersWithAccess = this.getUsersWithAccess(workItem) || [];
      const defaultCoReporterWithoutAccess = !usersWithAccess.map(u => u.userName).includes(defaultCoReader);

      if (defaultCoReporterWithoutAccess) {
        const dialogType =
          defaultCoReader && defaultCoReader != NativeAcademicWorkflowService.IGNORE_NAW_DURING_AA
            ? DialogType.OVERRIDE_DEFAULT_COREADER
            : DialogType.COREADER_NO_DEFAULT;

        const dialogData = {
          dataSource: this.getNawUserList(workItem, dialogType),
          dialogType: dialogType
        };

        const afterClosed$ = this.openModal(workItem, dialogData, 'naw.coreaderdialog.header').afterClosed;
        afterClosed$.subscribe(selection => {
          const username = selection?.userName || NativeAcademicWorkflowService.IGNORE_NAW_DURING_AA;
          this.defaultCoReaderOverrides.set(workItem.id, username);
        });
        return afterClosed$.toPromise();
      }
    }
    return Promise.resolve(false);
  }

  private handleBeforeCloseEvents(workItem, lifecycle) {
    if (this.isAppConfigActive() && !this.isReportDeleted(workItem)) {
      if (!this.data[workItem.id]) {
        this.data[workItem.id] = {};
      }

      this.data[workItem.id].beforeCloseworkItem = this.utilService.getTabWorkItem(workItem.id);
      this.data[workItem.id].examContext = this.utilService.getTabContext(workItem.id);

      if (this.integrationStatusService.isReportingType(appConstants.reportingAppName.PS)) {
        this.data[workItem.id].currentAssociationList =
          workItem.associationList &&
          workItem.associationList.map(id => {
            const acc = this.tabContextService.getAccNumber(id, workItem.id);
            return { examId: id, accessionNumber: acc };
          });
      } else {
        const assocList = this.tabContextService.getAssociatedExams(workItem.id);
        this.data[workItem.id].currentAssociationList =
          assocList && assocList.filter(assocInfo => assocInfo.examId != workItem.id);
      }
    }
    lifecycle.done();
  }

  private afterEvents() {
    this.eventService.afterEvent(
      Events.close.name,
      workItem => {
        this.startNaw(workItem);
      },
      EventType.LOCAL
    );
    this.eventService.afterEvent(
      Events.closediscard.name,
      workItem => {
        this.startNaw(workItem);
      },
      EventType.LOCAL
    );
    this.eventService.afterEvent(
      Events.read.name,
      workItem => {
        this.fillOriginalAssociationList(workItem);
      },
      EventType.LOCAL
    );
    this.eventService.afterEvent(
      Events.preview.name,
      workItem => {
        this.checkCoReporterAccess(workItem);
      },
      EventType.LOCAL
    );
  }

  private fillOriginalAssociationList(workItem) {
    if (!this.isAppConfigActive() || workItem.id === -1) {
      return;
    }

    if (!this.data[workItem.id]) {
      this.data[workItem.id] = {};
    }
    if (this.integrationStatusService.isReportingType(appConstants.reportingAppName.PS)) {
      this.nprDataService.getAssociationList(workItem.id).then(associatedExams => {
        this.data[workItem.id].associatedExamsFromDB = associatedExams.filter(
          assocInfo => assocInfo.examId != workItem.id
        );
      });
    } else {
      const list = this.tabContextService.getAssociatedExams(workItem.id);
      if (list) {
        this.data[workItem.id].associatedExamsFromDB = list.filter(assocInfo => assocInfo.examId != workItem.id);
      }
    }
  }

  /*
    Do not use the workItem outside of this method, it contains limited information.
    Use the beforeCloseworkItem, which was read from the tab context!!!
   */
  private startNaw(workItem) {
    if (!this.isAppConfigActive() || this.isReportDeleted(workItem)) {
      return;
    }

    const workItemData = this.data[workItem.id];
    if (workItemData && workItemData.beforeCloseworkItem) {
      let associatedExamsFromDB = workItemData.associatedExamsFromDB;
      const examContext = workItemData.examContext;
      const beforeEventWorkItem = workItemData.beforeCloseworkItem;
      let currentAssociationList = workItemData.currentAssociationList;

      //These have to be cleared here. Pass the local variables to later calls.
      this.data[workItem.id] = null;

      // In case of PS the tab's workitem is not refreshed, the event's workitem contains the proper status.
      // For some events the reportStatus is empty for SR, in this case the tab's workitem contains the status.
      let reportStatus = this.getWorkItemReportStatus(workItem);
      if (!reportStatus) {
        reportStatus = this.getWorkItemReportStatus(beforeEventWorkItem);
      }

      // If the context is 'preview' or 'launchreadonly', then we have to fetch the current assoc list now.
      // In this case the starting list and the current list is the same.
      if (examContext === 'preview' || examContext === 'launchreadonly') {
        this.nprDataService.getAssociationList(workItem.id).then(associatedExams => {
          currentAssociationList = associatedExams.filter(assocInfo => assocInfo.examId != workItem.id);
          associatedExamsFromDB = currentAssociationList;

          this.startNawUsingPrefetchedData(
            associatedExamsFromDB,
            examContext,
            currentAssociationList,
            workItem.ignoreNAW,
            beforeEventWorkItem,
            reportStatus
          );
        });
      } else {
        this.startNawUsingPrefetchedData(
          associatedExamsFromDB,
          examContext,
          currentAssociationList,
          workItem.ignoreNAW,
          beforeEventWorkItem,
          reportStatus
        );
      }
    }
  }

  private startNawUsingPrefetchedData(
    associatedExamsFromDB: any[],
    examContext: string,
    currentAssociationList: any[],
    ignoreNAW: boolean,
    beforeEventWorkItem,
    reportStatus
  ) {
    const lists = this.getAssociationLists(associatedExamsFromDB, examContext, currentAssociationList);
    const removedAssociationList = lists.removedAssociationList;
    currentAssociationList = lists.currentAssociationList;

    if (this.isActive() && !ignoreNAW && beforeEventWorkItem.keys.repositoryType != 'GWL') {
      // Use default coreader if AA is active
      let coreader = this.getDefaultCoreader();
      if (!coreader) {
        // Coreader not defined means AA is off
        this.showNativeAcademicWorkflowDialog(
          beforeEventWorkItem,
          reportStatus,
          currentAssociationList,
          removedAssociationList,
          associatedExamsFromDB,
          examContext
        );
      } else {
        // Coreader defined means AA is on
        if (reportStatus === REPORT_STATUS.PRELIM) {
          // If the default co-reader cannot access the exam due to institute separation, then we check the overrides
          coreader = this.defaultCoReaderOverrides.get(beforeEventWorkItem.id) || coreader;
          if (!coreader || coreader === NativeAcademicWorkflowService.IGNORE_NAW_DURING_AA) {
            return;
          }
          this.selectCoReaderOrReject(
            beforeEventWorkItem,
            coreader,
            false,
            currentAssociationList,
            removedAssociationList
          );
        } else if (reportStatus === REPORT_STATUS.DRAFT && this.isInCoReportingWorklist(beforeEventWorkItem)) {
          const nawStateCreator = this.getNawStateCreator(beforeEventWorkItem);
          this.selectCoReaderOrReject(
            beforeEventWorkItem,
            nawStateCreator,
            true,
            currentAssociationList,
            removedAssociationList
          );
        }
      }
    } else {
      this.handleAssociation(
        currentAssociationList,
        removedAssociationList,
        beforeEventWorkItem,
        associatedExamsFromDB,
        examContext
      );
    }
  }

  private getAssociationLists(associatedExamsFromDB: any[], examContext, currentAssociationList: any[]) {
    let removedAssociationList = null;
    // In preview or launchreadonly the association list cannot change.
    if (!(examContext === 'preview' || examContext === 'launchreadonly')) {
      removedAssociationList = this.calculateRemovedAssociationList(currentAssociationList, associatedExamsFromDB);
    }

    currentAssociationList =
      currentAssociationList && currentAssociationList.length === 0 ? null : currentAssociationList;
    removedAssociationList =
      removedAssociationList && removedAssociationList.length === 0 ? null : removedAssociationList;
    return { currentAssociationList, removedAssociationList };
  }

  private getWorkItemReportStatus(workItem) {
    return workItem?.reportStatus || workItem?.keys?.reportStatus;
  }

  private isReportDeleted(workItem) {
    const reportStatus = this.getWorkItemReportStatus(workItem);
    return reportStatus === REPORT_STATUS.DISCARD;
  }

  private getNawStateCreator(workItem) {
    const state = this.getState(workItem);
    return state?.nawState?.creator?.userName;
  }

  private openModal(workItem: any, data: any, headerTextKey: string) {
    const headerText = this.translate.instant(headerTextKey);
    data.acc = workItem?.keys?.procedureAccessionNo || workItem?.procedureAccessionNo || '';

    this.cleanupModals(workItem?.id);

    const modal = this.modalService.openModal(CoReaderDialogComponent, {
      headerContent: headerText,
      data
    });
    this.modals.set(workItem?.id, modal);
    return modal;
  }

  private cleanupModals(id: number) {
    this.modals.get(id)?.close();
    this.modals.delete(id);
  }

  private calculateRemovedAssociationList(associationList: any[], startingAssociations: any[]) {
    associationList = !!associationList ? associationList : [];
    if (Array.isArray(startingAssociations)) {
      return startingAssociations.filter(
        startingAssocInfo =>
          !associationList.find(endingAssocInfo => startingAssocInfo.examId === endingAssocInfo.examId)
      );
    }
    return [];
  }
}
